
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, X, CheckCircle } from 'lucide-react';
import { Token } from '../../../types';
import { TokenIcon, ChainIcon } from '../../ui/ChainIcons';

interface TokenSelectorProps {
    label: string;
    selected: Token | null;
    onSelect: (token: Token) => void;
    balances: Token[];
    chainFilter?: 'SOL' | 'SUI';
}

export const TokenSelector: React.FC<TokenSelectorProps> = ({ 
    selected, onSelect, balances, chainFilter
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const modalRef = useRef<HTMLDivElement>(null);

    const filteredTokens = useMemo(() => {
        return balances.filter(t => {
            const matchesChain = chainFilter ? t.chain === chainFilter : true;
            const matchesSearch = t.symbol.toLowerCase().includes(search.toLowerCase()) || 
                                 t.name.toLowerCase().includes(search.toLowerCase());
            return matchesChain && matchesSearch;
        });
    }, [balances, chainFilter, search]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className="relative">
            <button 
                onClick={() => setIsOpen(true)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-sm border transition-all duration-300 ${isOpen ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black border-transparent shadow-xl' : 'bg-stone-50 dark:bg-white/5 border-stone-200 dark:border-white/10 hover:border-stone-300 dark:hover:border-white/20'}`}
            >
                {selected ? (
                    <div className="flex items-center gap-2">
                        <TokenIcon symbol={selected.symbol} fallback={selected.icon} className="w-6 h-6 rounded-full shadow-sm" />
                        <span className="font-black text-[11px] uppercase tracking-widest">{selected.symbol}</span>
                    </div>
                ) : (
                    <span className="font-black text-[11px] uppercase tracking-widest">Select</span>
                )}
                <ChevronDown size={14} className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.98 }}
                        className="absolute right-0 top-full mt-3 z-[100] w-72 bg-white dark:bg-[#1A1D23] border border-stone-200 dark:border-[#2D3142] rounded-sm shadow-[0_30px_60px_-12px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col max-h-[400px]"
                        ref={modalRef}
                    >
                        <div className="p-4 border-b border-stone-100 dark:border-white/5 bg-gray-50 dark:bg-white/[0.01]">
                            <div className="relative group">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-nobel-gold transition-colors" />
                                <input 
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Filter assets..."
                                    className="w-full bg-white dark:bg-black/20 text-[10px] uppercase font-bold tracking-widest p-2.5 pl-9 outline-none border border-stone-200 dark:border-white/10 rounded-sm focus:border-nobel-gold"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto no-scrollbar py-1">
                            {filteredTokens.map(t => (
                                <button 
                                    key={t.mint}
                                    onClick={() => { onSelect(t); setIsOpen(false); }}
                                    className={`w-full flex items-center justify-between p-4 hover:bg-stone-50 dark:hover:bg-white/[0.05] transition-all group ${selected?.mint === t.mint ? 'bg-nobel-gold/5' : ''}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <TokenIcon symbol={t.symbol} fallback={t.icon} className="w-8 h-8 group-hover:scale-110 transition-transform" />
                                            <div className="absolute -bottom-1 -right-1">
                                                <ChainIcon chain={t.chain} className="w-3 h-3 border border-white dark:border-[#1A1D23] rounded-full" />
                                            </div>
                                        </div>
                                        <div className="text-left">
                                            <div className="font-bold text-[#0A0B0F] dark:text-white text-xs">{t.symbol}</div>
                                            <div className="text-[9px] text-stone-400 uppercase font-black tracking-tighter">{t.name}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] font-mono font-bold text-[#0A0B0F] dark:text-white">{t.balance.toLocaleString()}</div>
                                        <div className="text-[8px] text-stone-400 font-mono">${t.priceUsd.toFixed(2)}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
